import React, { useState, useEffect } from 'react'
import {
  Box,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material'

interface SettingsSwitchProps {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}

export const SettingsSwitch: React.FC<SettingsSwitchProps> = ({ label, checked, onChange }) => (
  <FormControlLabel
    control={
      <Switch
        size="small"
        checked={checked}
        onChange={(_, v) => onChange(v)}
      />
    }
    label={<Typography variant="body2">{label}</Typography>}
    sx={{ ml: 0 }}
  />
)

interface SettingsSelectProps {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}

export const SettingsSelect: React.FC<SettingsSelectProps> = ({ label, value, options, onChange }) => (
  <TextField
    select
    label={label}
    value={value}
    onChange={e => onChange(e.target.value)}
    size="small"
    fullWidth
    sx={{ mt: 1 }}
  >
    {options.map(opt => (
      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
    ))}
  </TextField>
)

interface SettingsNumberInputProps {
  label: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
}

export const SettingsNumberInput: React.FC<SettingsNumberInputProps> = ({ label, value, onChange, min = 0, max = 9 }) => {
  const [localValue, setLocalValue] = useState(String(value))

  // Sync when parent value changes externally
  useEffect(() => {
    const parsed = parseInt(localValue, 10)
    if (isNaN(parsed) || parsed !== value) {
      setLocalValue(String(value))
    }
  }, [value])

  const handleBlur = () => {
    const parsed = parseInt(localValue, 10)
    if (isNaN(parsed) || localValue.trim() === '') {
      setLocalValue(String(min))
      onChange(min)
    } else {
      const clamped = Math.min(max, Math.max(min, parsed))
      setLocalValue(String(clamped))
      onChange(clamped)
    }
  }

  return (
    <TextField
      label={label}
      type="number"
      value={localValue}
      onChange={e => setLocalValue(e.target.value)}
      onBlur={handleBlur}
      size="small"
      inputProps={{ min, max }}
      sx={{ mt: 1, width: 120 }}
    />
  )
}

interface SettingsTextFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  fullWidth?: boolean
}

export const SettingsTextField: React.FC<SettingsTextFieldProps> = ({ label, value, onChange, placeholder, fullWidth = true }) => (
  <TextField
    label={label}
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    size="small"
    fullWidth={fullWidth}
    sx={{ mt: 1 }}
  />
)

export const SettingsRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
    {children}
  </Box>
)
